<script setup lang="ts">
  import { minidenticon } from 'minidenticons'
  import { computed } from 'vue'
  import { useAuth } from '~renderer/composables/useAuth'
  import { isAnonymousUser, isAuthenticatedUser } from '~shared/util/user'

  const { user, signOut, hasPatreonTier, isAdmin, isModerator } = useAuth()
  const fallbackAvatar = computed(() => minidenticon(user.value?.id ?? 'unknown'))
</script>

<template>
  <header
    class="relative z-10 flex items-center justify-between bg-purple-500 px-6 py-4 text-white md:px-12"
  >
    <p>
      <a href="/" class="flex items-center gap-2">
        <!-- Logo -->
        <img src="/images/branding/icon.svg" alt="Turnip Logo" class="h-8 w-auto" />
        <img
          src="/images/branding/wordmark.svg"
          alt="Turnip Exchange"
          class="hidden h-6 w-auto sm:block"
        />
      </a>
    </p>

    <div class="flex items-center gap-6">
      <!-- Social Icons -->
      <nav class="flex items-center gap-4">
        <!-- Twitter -->
        <a href="https://twitter.com/WarpWorld" class="hover:text-tan-200 transition-colors">
          <span class="sr-only">Twitter</span>
          <i-fa7-brands-twitter />
        </a>
        <!-- Discord -->
        <a href="https://discord.gg/rKCtJwDSry" class="hover:text-tan-200 transition-colors">
          <span class="sr-only">Discord</span>
          <i-fa7-brands-discord />
        </a>
        <!-- Patreon -->
        <a href="https://patreon.warp.world/" class="hover:text-tan-200 transition-colors">
          <span class="sr-only">Patreon</span>
          <i-fa7-brands-patreon />
        </a>
      </nav>

      <div
        v-if="
          user &&
          (isAnonymousUser(user) || (isAuthenticatedUser(user) && !user.usernameResetRequired))
        "
        class="flex items-center gap-4"
      >
        <div class="hidden text-right md:block">
          <p class="text-tan-200 text-xs font-bold uppercase">Welcome</p>
          <p class="leading-none font-bold text-white">{{ user.name }}</p>
        </div>
        <a
          href="/user"
          v-if="isAuthenticatedUser(user)"
          class="group relative transition-transform hover:scale-110"
        >
          <img
            v-if="user.image"
            :src="user.image"
            class="size-10 rounded-full border-2"
            :class="[hasPatreonTier(1) ? 'border-red-600' : 'border-tan-200']"
            :alt="user.name"
          />
          <i-fa7-solid-crown
            v-if="isAdmin"
            class="absolute top-0 -right-1 size-4 rotate-45 text-yellow-300 drop-shadow-sm drop-shadow-black"
          />
          <i-fa7-solid-pencil
            v-else-if="isModerator"
            class="absolute top-0 -right-1 size-4 text-yellow-300 drop-shadow-sm drop-shadow-black"
          />
          <i-fa7-solid-star
            v-else-if="hasPatreonTier(1)"
            class="absolute top-0 -right-1 size-4 text-yellow-300 drop-shadow-sm drop-shadow-black"
          />
        </a>
        <div
          v-else
          v-html="fallbackAvatar"
          class="border-tan-200 size-10 overflow-hidden rounded-full border-2 bg-slate-50"
        ></div>
        <div class="flex items-center gap-2" v-if="user">
          <button
            @click="signOut"
            class="corner-squircle bg-tan-700 hover:bg-tan-600 cursor-pointer rounded-lg px-4 py-2 text-sm font-bold text-white transition-colors"
          >
            Logout
          </button>
        </div>
      </div>
      <a
        v-if="!user || isAnonymousUser(user)"
        href="/auth/signin"
        class="corner-squircle cursor-pointer rounded-lg bg-green-400 px-6 py-2 font-bold text-black transition-colors hover:bg-green-500"
      >
        Log In
      </a>
    </div>
  </header>
</template>
